import axios from 'axios';
import { API_BASE_URL } from '@config';
import { showMessage } from './fuse/message.actions';

// Action Types - Configurations
export const FETCH_CONFIGURATIONS_REQUEST = '[GLOBAL_PROVISION] FETCH CONFIGURATIONS REQUEST';
export const FETCH_CONFIGURATIONS_SUCCESS = '[GLOBAL_PROVISION] FETCH CONFIGURATIONS SUCCESS';
export const FETCH_CONFIGURATIONS_FAILURE = '[GLOBAL_PROVISION] FETCH CONFIGURATIONS FAILURE';

export const FETCH_CONFIGURATION_REQUEST = '[GLOBAL_PROVISION] FETCH CONFIGURATION REQUEST';
export const FETCH_CONFIGURATION_SUCCESS = '[GLOBAL_PROVISION] FETCH CONFIGURATION SUCCESS';
export const FETCH_CONFIGURATION_FAILURE = '[GLOBAL_PROVISION] FETCH CONFIGURATION FAILURE';

export const CREATE_CONFIGURATION_REQUEST = '[GLOBAL_PROVISION] CREATE CONFIGURATION REQUEST';
export const CREATE_CONFIGURATION_SUCCESS = '[GLOBAL_PROVISION] CREATE CONFIGURATION SUCCESS';
export const CREATE_CONFIGURATION_FAILURE = '[GLOBAL_PROVISION] CREATE CONFIGURATION FAILURE';

export const UPDATE_CONFIGURATION_REQUEST = '[GLOBAL_PROVISION] UPDATE CONFIGURATION REQUEST';
export const UPDATE_CONFIGURATION_SUCCESS = '[GLOBAL_PROVISION] UPDATE CONFIGURATION SUCCESS';
export const UPDATE_CONFIGURATION_FAILURE = '[GLOBAL_PROVISION] UPDATE CONFIGURATION FAILURE';

export const PARTIAL_UPDATE_CONFIGURATION_REQUEST = '[GLOBAL_PROVISION] PARTIAL UPDATE CONFIGURATION REQUEST';
export const PARTIAL_UPDATE_CONFIGURATION_SUCCESS = '[GLOBAL_PROVISION] PARTIAL UPDATE CONFIGURATION SUCCESS';
export const PARTIAL_UPDATE_CONFIGURATION_FAILURE = '[GLOBAL_PROVISION] PARTIAL UPDATE CONFIGURATION FAILURE';

export const DELETE_CONFIGURATION_REQUEST = '[GLOBAL_PROVISION] DELETE CONFIGURATION REQUEST';
export const DELETE_CONFIGURATION_SUCCESS = '[GLOBAL_PROVISION] DELETE CONFIGURATION SUCCESS';
export const DELETE_CONFIGURATION_FAILURE = '[GLOBAL_PROVISION] DELETE CONFIGURATION FAILURE';

// Action Types - Execution Logs
export const FETCH_EXECUTION_LOGS_REQUEST = '[GLOBAL_PROVISION] FETCH EXECUTION LOGS REQUEST';
export const FETCH_EXECUTION_LOGS_SUCCESS = '[GLOBAL_PROVISION] FETCH EXECUTION LOGS SUCCESS';
export const FETCH_EXECUTION_LOGS_FAILURE = '[GLOBAL_PROVISION] FETCH EXECUTION LOGS FAILURE';

// Action Types - UI
export const CLEAR_CURRENT_CONFIGURATION = '[GLOBAL_PROVISION] CLEAR CURRENT CONFIGURATION';
export const SET_FILTER = '[GLOBAL_PROVISION] SET FILTER';

/**
 * Fetch all Global Provision Configurations
 */
export const fetchConfigurations = (params = {}) => async (dispatch) => {
    dispatch({ type: FETCH_CONFIGURATIONS_REQUEST });
    
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${API_BASE_URL}/api/globalProvisionConfigs${queryString ? '?' + queryString : ''}`;
      
      const response = await axios.get(url);
      
      dispatch({
        type: FETCH_CONFIGURATIONS_SUCCESS,
        payload: response.data
      });
      
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch configurations';
      
      dispatch({
        type: FETCH_CONFIGURATIONS_FAILURE,
        payload: errorMessage
      });
      
      dispatch(showMessage({ message: errorMessage, variant: 'error' }));
      throw error;
    }
};

/**
 * Fetch a single global provision configuration by ID
 */
export function fetchConfiguration(id) {
  return async (dispatch) => {
    dispatch({ type: FETCH_CONFIGURATION_REQUEST });
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/globalProvisionConfigs/${id}`
      );

      dispatch({
        type: FETCH_CONFIGURATION_SUCCESS,
        payload: response.data
      });

      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch configuration';
      
      dispatch({
        type: FETCH_CONFIGURATION_FAILURE,
        payload: errorMessage
      });

      throw error;
    }
  };
}

/**
 * Create a new global provision configuration
 */
export function createConfiguration(data) {
  return async (dispatch) => {
    dispatch({ type: CREATE_CONFIGURATION_REQUEST });
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/globalProvisionConfigs`,
        data
      );

      dispatch({
        type: CREATE_CONFIGURATION_SUCCESS,
        payload: response.data
      });

      dispatch(showMessage({ message: 'Configuration created successfully', variant: 'success' }));

      // Refresh the list after creation
      dispatch(fetchConfigurations());

      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create configuration';
      
      dispatch({
        type: CREATE_CONFIGURATION_FAILURE,
        payload: errorMessage
      });

      dispatch(showMessage({ message: errorMessage, variant: 'error' }));
      throw error;
    }
  };
}

/**
 * Update an existing global provision configuration (full update)
 */
export function updateConfiguration(id, data) {
  return async (dispatch) => {
    dispatch({ type: UPDATE_CONFIGURATION_REQUEST });
    
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/globalProvisionConfigs/${id}`,
        data
      );

      dispatch({
        type: UPDATE_CONFIGURATION_SUCCESS,
        payload: response.data
      });

      dispatch(showMessage({ message: 'Configuration updated successfully', variant: 'success' }));

      // Refresh the list after update
      dispatch(fetchConfigurations());

      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update configuration';
      
      dispatch({
        type: UPDATE_CONFIGURATION_FAILURE,
        payload: errorMessage
      });

      dispatch(showMessage({ message: errorMessage, variant: 'error' }));
      throw error;
    }
  };
}

/**
 * Partially update a global provision configuration
 */
export function partialUpdateConfiguration(id, data) {
  return async (dispatch) => {
    dispatch({ type: PARTIAL_UPDATE_CONFIGURATION_REQUEST });
    
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/globalProvisionConfigs/${id}`,
        data
      );

      dispatch({
        type: PARTIAL_UPDATE_CONFIGURATION_SUCCESS,
        payload: response.data
      });

      dispatch(showMessage({ message: 'Configuration updated successfully', variant: 'success' }));

      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update configuration';
      
      dispatch({
        type: PARTIAL_UPDATE_CONFIGURATION_FAILURE,
        payload: errorMessage
      });

      dispatch(showMessage({ message: errorMessage, variant: 'error' }));
      throw error;
    }
  };
}

/**
 * Delete a global provision configuration
 */
export function deleteConfiguration(id) {
  return async (dispatch) => {
    dispatch({ type: DELETE_CONFIGURATION_REQUEST });
    
    try {
      await axios.delete(
        `${API_BASE_URL}/api/globalProvisionConfigs/${id}`
      );

      dispatch({
        type: DELETE_CONFIGURATION_SUCCESS,
        payload: id
      });

      dispatch(showMessage({ message: 'Configuration deleted successfully', variant: 'success' }));

      return id;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete configuration';
      
      dispatch({
        type: DELETE_CONFIGURATION_FAILURE,
        payload: errorMessage
      });

      dispatch(showMessage({ message: errorMessage, variant: 'error' }));
      throw error;
    }
  };
}

/**
 * Fetch execution logs for a configuration
 */
export function fetchExecutionLogs(configId, params = {}) {
  return async (dispatch) => {
    dispatch({ type: FETCH_EXECUTION_LOGS_REQUEST });
    
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${API_BASE_URL}/api/globalProvisionConfigs/${configId}/logs${queryString ? '?' + queryString : ''}`;
      
      const response = await axios.get(url);

      dispatch({
        type: FETCH_EXECUTION_LOGS_SUCCESS,
        payload: response.data
      });

      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch execution logs';
      
      dispatch({
        type: FETCH_EXECUTION_LOGS_FAILURE,
        payload: errorMessage
      });

      dispatch(showMessage({ message: errorMessage, variant: 'error' }));
      throw error;
    }
  };
}

/**
 * Clear current configuration from state
 */
export function clearCurrentConfiguration() {
  return {
    type: CLEAR_CURRENT_CONFIGURATION
  };
}

/**
 * Set filter for configurations list
 */
export function setFilter(filter) {
  return {
    type: SET_FILTER,
    payload: filter
  };
}
